<template>
  <v-switch :model-value="value"
            v-bind="$attrs"
            inset
            :ripple="false"
            :disabled="disabled || loading"
            :loading="loading"
            class="AppToggle"
            :class="{ 'AppToggle--loading': loading }"
            @update:model-value="$emit('input', !!$event)"
  >
    <template #thumb>
      <div v-if="loading" class="v-switch__thumb">
        <v-progress-circular indeterminate size="16" width="2" color="icon-primary"></v-progress-circular>
      </div>
    </template>
  </v-switch>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

/**
 * It's used to switch the state of a single option. The inline label of "Switch" should be displayed clearly, e.g. Disable/Enable, Disallow/Allow etc.
 * All vuetify props are available
 * @displayName Toggle
 */

export default defineComponent({
  name: 'AppToggle',
  inheritAttrs: false,
  props: {
    /**
     * The v-model bound value
     * */
    value: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    /**
     * Displays circular progress bar.
     * */
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * Disable the input
     * */
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
})

</script>

<style scoped lang="scss">
.AppToggle {
  :deep(.v-switch__track) {
    height: 20px;
    min-width: 36px;
    opacity: 1;
    background-color: rgb(var(--v-theme-surface-grey));
    transition: background-color .2s ease, box-shadow .2s ease;
  }

  // On state
  :deep(.v-selection-control--dirty .v-switch__track) {
    background-color: rgb(var(--v-theme-surface-info-bold)) !important;
  }

  :deep(.v-selection-control) {
    opacity: 1 !important;
  }

  :deep(.v-selection-control__input::before) {
    opacity: 0 !important;
  }

  // Thumb (white in every state)
  :deep(.v-switch__thumb) {
    transform: scale(1);
    height: 16px;
    width: 16px;
    background-color: rgb(var(--v-theme-surface-primary));
    color: rgb(var(--v-theme-surface-primary));
  }

  :deep(*) {
    .v-selection-control__input {
      transform: translateX(-8px) !important;
    }

    .v-selection-control--dirty .v-selection-control__input {
      transform: translateX(8px) !important;
    }
  }

  // Hover - off: base color + hover overlay
  &:hover :deep(.v-switch__track) {
    background-image: linear-gradient(
      rgba(var(--v-theme-surface-hover)),
      rgba(var(--v-theme-surface-hover))
    );
  }

  // Hover - on: dedicated darker token
  &:hover :deep(.v-selection-control--dirty .v-switch__track) {
    background-color: rgb(var(--v-theme-surface-info-bold-hover)) !important;
    background-image: none;
  }

  // Focus ring (keyboard focus only)
  :deep(.v-selection-control:has(input:focus-visible) .v-switch__track) {
    box-shadow: 0 0 0 3px rgb(var(--v-theme-sky-400));
  }

  // Disabled (not while loading - loading keeps its colored track)
  &.v-input--disabled:not(.AppToggle--loading) {
    :deep(.v-switch__track) {
      background-color: rgb(var(--v-theme-surface-neutral)) !important;
      background-image: none;
      opacity: 1;
    }

    :deep(.v-label) {
      color: rgb(var(--v-theme-text-disabled));
    }

    :deep(.v-selection-control--dirty .v-switch__track) {
      background-color: rgb(var(--v-theme-surface-info)) !important;
    }
  }

  :deep(.v-label) {
    color: rgb(var(--v-theme-text-secondary));
  }

}
</style>
